/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/** Captured feedback form. Field names mirror the backend `FeedbackForm`; all optional. */
export interface FeedbackForm {
  date?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  dealerName?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  program?: string;
  engineeringRelease?: string;
  urlViewSelection?: string;
  issueDescription?: string;
}

/** Feedback submission body. `userId` is the caller's GMIN, `type` is INTERNAL or EXTERNAL. */
export interface UserFeedbackRequest {
  feedbackForm: FeedbackForm;
  url: string;
  userId: string;
  userName: string;
  plant?: string;
  type: 'INTERNAL' | 'EXTERNAL';
}

/** Submits feedback and returns the feedback id used for attachments / email. */
export async function submitFeedback(request: UserFeedbackRequest): Promise<number> {
  const { data } = await apiClient.post<number>('/ediagnostics/v1/user-feedback', request);
  return data;
}

/** Uploads attachment(s) (screenshot, files) against a submitted feedback id. */
export async function uploadFeedbackDocs(id: number, files: File[]): Promise<boolean> {
  if (!files.length) return true;
  const form = new FormData();
  files.forEach((file) => form.append('documents', file));
  const { data } = await apiClient.post<boolean>(
    `/ediagnostics/v1/user-feedback/docs/${id}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Triggers the server-side notifications for a submitted feedback id: the Slack
 * channel post and the notification email. Both are best-effort on the server;
 * the boolean reports only whether the email was sent.
 */
export async function sendFeedbackNotifications(id: number): Promise<boolean> {
  const { data } = await apiClient.post<boolean>(`/ediagnostics/v1/user-feedback/send/id/${id}`);
  return data;
}

/** Create record, upload attachments, notify (Slack + email). */
export async function submitFeedbackFlow(
  request: UserFeedbackRequest,
  files: File[] = [],
): Promise<number> {
  const id = await submitFeedback(request);
  if (files.length) {
    await uploadFeedbackDocs(id, files).catch(() => false);
  }
  await sendFeedbackNotifications(id).catch(() => false);
  return id;
}

/** Summary row for a single submitted feedback item (admin list view). */
export interface FeedbackSummary {
  feedbackId: number;
  submissionDate: string | null;
  type: string;
  userName: string | null;
  userId: string | null;
  issueDescription: string | null;
  vin: string | null;
  url: string | null;
}

/** One page of the admin feedback list. `page` is zero-based; `total` counts every page. */
export interface FeedbackPage {
  items: FeedbackSummary[];
  total: number;
  page: number;
  size: number;
}

/** Admin list filters. `from` / `to` are inclusive `yyyy-MM-dd` days in the browser's time zone. */
export interface FeedbackListParams {
  search?: string;
  from?: string;
  to?: string;
  /** Zero-based. */
  page?: number;
  size?: number;
}

/** Attachment metadata for a feedback submission. */
export interface FeedbackAttachment {
  documentId: number;
  name: string;
  type?: string;
}

/** Full detail for a single feedback submission, including all form fields and attachments. */
export interface FeedbackDetail {
  feedbackId: number;
  attachments: FeedbackAttachment[];
  form: FeedbackForm | null;
  submissionDate: string | null;
  userName: string | null;
  userId: string | null;
  url: string | null;
  type: string;
}

/** Local midnight starting `yyyy-MM-dd`, `days` later, as an instant the server can compare. */
function localMidnight(day: string, days = 0): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d + days).toISOString();
}

/**
 * One page of user-submitted feedback, newest first, filtered and paged on the server.
 *
 * The day range becomes a half-open window of instants (the start of `from` up to the start of
 * the day after `to`), so "the 14th" means the 14th where the admin is rather than on the server.
 */
export async function listFeedback(params: FeedbackListParams = {}): Promise<FeedbackPage> {
  const { search, from, to, page, size } = params;
  const { data } = await apiClient.get<FeedbackPage>('/ediagnostics/v1/admin/feedback', {
    params: {
      search: search?.trim() || undefined,
      submittedFrom: from ? localMidnight(from) : undefined,
      submittedBefore: to ? localMidnight(to, 1) : undefined,
      page,
      size,
    },
  });
  return data;
}

/** Returns the full detail for a single feedback submission. */
export async function getFeedbackDetail(id: number): Promise<FeedbackDetail> {
  const { data } = await apiClient.get<FeedbackDetail>(`/ediagnostics/v1/admin/feedback/${id}`);
  return data;
}

/** Downloads a feedback attachment as a browser file download. */
export async function downloadFeedbackDoc(
  feedbackId: number,
  documentId: number,
  name: string,
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/ediagnostics/v1/admin/feedback/${feedbackId}/docs/${documentId}`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
